import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Upload, Trash2, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { DOC_CATEGORY_LABELS, formatDateShort, downloadHRDocument } from './hrUtils';

export const DocsAdminTab = () => {
  const [docs, setDocs] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ category: 'paystub', title: '', employee_id: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [filterCat, setFilterCat] = useState('all');

  const load = useCallback(() => {
    hrAPI.listDocuments({}).then((res) => setDocs(res.data)).catch(() => setDocs([]));
  }, []);

  useEffect(() => {
    load();
    hrAPI.listEmployees().then((res) => setEmployees(res.data)).catch(() => {});
  }, [load]);

  const needsEmployee = ['paystub', 'tax'].includes(form.category);

  const upload = async () => {
    if (!form.file) { toast.error('Choose a file'); return; }
    if (!form.title.trim()) { toast.error('Enter a title'); return; }
    if (needsEmployee && !form.employee_id) { toast.error('Select an employee'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('category', form.category);
      fd.append('title', form.title);
      fd.append('employee_id', needsEmployee || form.employee_id ? form.employee_id : '');
      await hrAPI.uploadDocument(fd);
      toast.success('Document uploaded');
      setForm({ category: form.category, title: '', employee_id: '', file: null });
      document.getElementById('hr-admin-file-input').value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this document?')) return;
    try {
      await hrAPI.deleteDocument(id);
      toast.success('Document deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (!docs) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  const filtered = filterCat === 'all' ? docs : docs.filter((d) => d.category === filterCat);

  return (
    <div className="space-y-6" data-testid="hr-admin-docs-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg flex items-center gap-2"><Upload className="w-5 h-5 text-cyan-500" /> Upload Document</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-slate-700">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="hr-admin-doc-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Pay Stub — May 2026" className="bg-white border-slate-200 mt-1" data-testid="hr-admin-doc-title-input" />
            </div>
            <div>
              <Label className="text-slate-700">Employee {needsEmployee ? '(required)' : '(optional)'}</Label>
              <Select value={form.employee_id || 'company'} onValueChange={(v) => setForm({ ...form, employee_id: v === 'company' ? '' : v })}>
                <SelectTrigger className="bg-white border-slate-200 mt-1" data-testid="hr-admin-doc-employee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company-wide (all employees)</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">File (max 10MB)</Label>
              <Input id="hr-admin-file-input" type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="bg-white border-slate-200 mt-1" data-testid="hr-admin-doc-file-input" />
            </div>
          </div>
          <Button onClick={upload} disabled={uploading} className="bg-cyan-500 hover:bg-cyan-600 rounded-full" data-testid="hr-admin-doc-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-slate-800 text-lg">All HR Documents <Badge variant="secondary" className="ml-2">{filtered.length}</Badge></CardTitle>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44 bg-white border-slate-200" data-testid="hr-admin-doc-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(DOC_CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="hr-admin-docs-list">
          {filtered.length === 0 && <p className="text-sm text-slate-400 py-4">No documents in this category yet.</p>}
          {filtered.map((d) => (
            <div key={d.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-cyan-500 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.title}</p>
                    <Badge variant="secondary">{DOC_CATEGORY_LABELS[d.category] || d.category}</Badge>
                  </div>
                  <p className="text-xs text-slate-400">
                    {d.employee_name ? `For ${d.employee_name}` : 'Company-wide'} · {d.file_name} · {formatDateShort(d.created_at)}
                  </p>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" className="rounded-full" onClick={() => downloadHRDocument(d)}><Download className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="rounded-full text-red-500 hover:text-red-600" onClick={() => remove(d.id)} data-testid={`hr-admin-doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
